import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../../../lib/apiClient";
import {
  ig, igGlobalStyles, Badge, StatusBadge, ChannelChip, Avatar, IconBtn, PrimaryBtn, GhostBtn, Tag, Skeleton, timeAgo, dayLabel, channelMeta,
} from "./_inbox_shared";
import {
  Search, Filter, ArrowUpDown, PenSquare, Star, Phone, Mail, MessageSquare,
  Sparkles, Paperclip, Send, Clock, Tag as TagIcon, MoreHorizontal, Inbox, Bell,
  CircleHelp, Bot, Mic, Languages, AlertOctagon, X, Plus, Settings as Cog,
  PhoneMissed, Voicemail, FileText, ShieldAlert, ChevronLeft, RefreshCw,
} from "lucide-react";
import ContactPanel from "./ContactPanel";

const VIEWS = [
  { key: "all",            label: "All",             icon: Inbox },
  { key: "unread",         label: "Unread",          icon: Bell },
  { key: "needs_reply",    label: "Needs reply",     icon: MessageSquare },
  { key: "human_takeover", label: "Human takeover",  icon: ShieldAlert },
  { key: "ai_handled",     label: "AI handled",      icon: Bot },
  { key: "starred",        label: "Starred",         icon: Star },
  { key: "missed_calls",   label: "Missed calls",    icon: PhoneMissed },
  { key: "tickets",        label: "Tickets",         icon: FileText },
  { key: "closed",         label: "Closed",          icon: X },
];

const CHANNEL_FILTERS = ["all", "sms", "email", "whatsapp", "instagram", "phone"];

export default function UnifiedInbox() {
  const [view, setView] = useState("all");
  const [channel, setChannel] = useState("all");
  const [q, setQ] = useState("");
  const [convs, setConvs] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const reload = useCallback(() => setRefreshTick((t) => t + 1), []);

  useEffect(() => {
    const url = new URLSearchParams();
    if (view !== "all") url.set("view", view);
    if (channel !== "all") url.set("channel", channel);
    if (q) url.set("q", q);
    const qs = url.toString();
    api.get(`/api/inbox/conversations${qs ? "?" + qs : ""}`)
      .then((d) => {
        setConvs(d.items || []);
        setSelectedId((cur) => cur || (d.items && d.items[0]?.id) || null);
      })
      .catch(() => setConvs([]));
  }, [view, channel, q, refreshTick]);

  return (
    <div style={{ background: ig.bg, color: ig.ink, minHeight: "calc(100vh - 0px)",
      margin: "-22px -26px -80px", fontFamily: "Inter, system-ui, -apple-system, sans-serif" }}
      data-testid="unified-inbox">
      <style>{igGlobalStyles}</style>

      <InboxHeader onCompose={reload} onRefresh={reload} />

      <div style={{ display: "grid", gridTemplateColumns: "340px 1fr 380px", gap: 0, height: "calc(100vh - 110px)", minHeight: 640 }}>
        {/* LEFT — Conversation list */}
        <LeftColumn
          view={view} setView={setView}
          channel={channel} setChannel={setChannel}
          q={q} setQ={setQ}
          convs={convs}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />

        {/* CENTER — Conversation workspace */}
        <CenterColumn convId={selectedId} onReload={reload} />

        {/* RIGHT — Contact panel (only when a conversation is selected) */}
        {selectedId ? <ContactPanel convId={selectedId} reloadTick={refreshTick} onChanged={reload} /> :
          <div style={rightEmptyStyle}>
            <div style={{ textAlign: "center", color: ig.muted, fontSize: 13 }}>
              <Avatar name="?" size={56} />
              <div style={{ marginTop: 12, fontWeight: 700, color: ig.ink }}>No conversation selected</div>
              <div style={{ marginTop: 4 }}>Contact, tickets, and CRM<br />will appear here.</div>
            </div>
          </div>
        }
      </div>
    </div>
  );
}

/* ─────────────────────────── HEADER ─────────────────────────── */

function InboxHeader({ onRefresh }) {
  return (
    <header style={{
      padding: "14px 22px", background: ig.card, borderBottom: `1px solid ${ig.border}`,
      display: "flex", alignItems: "center", gap: 16,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10, display: "grid", placeItems: "center",
        background: `linear-gradient(135deg, ${ig.emerald}, ${ig.green})`, color: "#fff",
        boxShadow: "0 6px 16px rgba(24,184,107,0.32)",
      }}>
        <Inbox size={18} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: -0.2, lineHeight: 1.1 }}>Unified Inbox</div>
        <div style={{ fontSize: 11, color: ig.muted, marginTop: 2 }}>
          Manage every client conversation across SMS, email, WhatsApp, calls, voicemail, social DMs, tickets, and AI — in one place.
        </div>
      </div>
      <div style={{ position: "relative" }}>
        <Search size={14} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: ig.mutedSoft }} />
        <input data-testid="inbox-global-search" placeholder="Search by name, phone, email, tag, message…"
          style={{
            width: 360, height: 36, padding: "0 12px 0 32px", borderRadius: 10,
            border: `1px solid ${ig.border}`, background: ig.bg, fontSize: 12.5, outline: "none", color: ig.ink,
          }} />
      </div>
      <IconBtn title="Refresh" onClick={onRefresh} data-testid="inbox-refresh"><RefreshCw size={14} /></IconBtn>
      <IconBtn title="Notifications" data-testid="inbox-notifs"><Bell size={14} /></IconBtn>
      <IconBtn title="AI settings"   data-testid="inbox-ai-settings"><Cog size={14} /></IconBtn>
      <PrimaryBtn data-testid="inbox-new-message"><PenSquare size={14} /> New message</PrimaryBtn>
    </header>
  );
}

/* ─────────────────────────── LEFT COLUMN ─────────────────────────── */

function LeftColumn({ view, setView, channel, setChannel, q, setQ, convs, selectedId, onSelect }) {
  return (
    <aside style={{
      borderRight: `1px solid ${ig.border}`, background: ig.card, display: "flex", flexDirection: "column", minWidth: 0,
    }}>
      <div style={{ padding: "14px 14px 8px" }}>
        <div style={{ position: "relative", marginBottom: 10 }}>
          <Search size={13} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: ig.mutedSoft }} />
          <input data-testid="inbox-conv-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search conversations…"
            style={{ width: "100%", height: 34, padding: "0 12px 0 30px", borderRadius: 9,
              border: `1px solid ${ig.border}`, background: ig.bg, fontSize: 12.5, outline: "none", color: ig.ink }} />
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <GhostBtn data-testid="inbox-filter"><Filter size={13} /> Filter</GhostBtn>
          <GhostBtn data-testid="inbox-sort"><ArrowUpDown size={13} /> Sort</GhostBtn>
          <div style={{ flex: 1 }} />
        </div>
      </div>
      <div className="ig-scroll" style={{ padding: "0 8px", overflowY: "auto", maxHeight: 220, flexShrink: 0 }}>
        {VIEWS.map((v) => {
          const Ic = v.icon;
          const active = v.key === view;
          return (
            <button key={v.key} data-testid={`view-${v.key}`} onClick={() => setView(v.key)} style={{
              display: "flex", alignItems: "center", gap: 9, width: "100%",
              padding: "8px 10px", borderRadius: 8, border: 0,
              background: active ? ig.mint : "transparent",
              color: active ? ig.forest : ig.ink,
              fontSize: 12.5, fontWeight: active ? 800 : 600, cursor: "pointer", textAlign: "left",
            }}>
              <Ic size={14} /> {v.label}
            </button>
          );
        })}
      </div>
      <div style={{ padding: "10px 14px 6px", borderTop: `1px solid ${ig.borderSoft}`,
        fontSize: 9.5, color: ig.mutedSoft, letterSpacing: "0.18em", fontWeight: 800, textTransform: "uppercase" }}>
        Channels
      </div>
      <div style={{ padding: "0 12px 10px", display: "flex", flexWrap: "wrap", gap: 6 }}>
        {CHANNEL_FILTERS.map((ch) => {
          const active = ch === channel;
          const m = channelMeta[ch];
          return (
            <button key={ch} data-testid={`channel-${ch}`} onClick={() => setChannel(ch)} style={{
              padding: "4px 10px", borderRadius: 999, border: `1px solid ${active ? ig.emerald : ig.border}`,
              background: active ? ig.mint : ig.card, color: active ? ig.forest : ig.ink,
              fontSize: 11, fontWeight: 700, cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 5,
            }}>
              {m ? <span>{m.icon}</span> : null}{ch === "all" ? "All" : m.label}
            </button>
          );
        })}
      </div>
      <div className="ig-scroll" style={{ flex: 1, overflowY: "auto", borderTop: `1px solid ${ig.borderSoft}` }}>
        {!convs ? (
          <div style={{ padding: 14, display: "grid", gap: 10 }}>
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} height={56} radius={10} />)}
          </div>
        ) : convs.length === 0 ? (
          <div style={{ padding: "40px 16px", textAlign: "center", color: ig.muted, fontSize: 12.5 }}>
            <Inbox size={28} style={{ color: ig.mutedSoft }} />
            <div style={{ marginTop: 8, fontWeight: 700, color: ig.ink }}>No conversations match</div>
            <div style={{ marginTop: 4 }}>Try clearing filters or search.</div>
          </div>
        ) : convs.map((c) => (
          <ConversationCard key={c.id} c={c} selected={c.id === selectedId} onSelect={() => onSelect(c.id)} />
        ))}
      </div>
    </aside>
  );
}

function ConversationCard({ c, selected, onSelect }) {
  const contact = c.contact || {};
  return (
    <button data-testid={`conv-card-${c.id}`} onClick={onSelect} style={{
      display: "block", width: "100%", padding: "12px 14px", textAlign: "left",
      border: 0, borderBottom: `1px solid ${ig.borderSoft}`,
      background: selected ? ig.mint : "transparent",
      borderLeft: `3px solid ${selected ? ig.emerald : "transparent"}`,
      cursor: "pointer",
    }}>
      <div style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
        <Avatar name={contact.full_name || "?"} size={36} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: ig.ink, flex: 1, minWidth: 0,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {contact.full_name || contact.phone || "Unknown"}
            </span>
            <span style={{ fontSize: 10.5, color: ig.muted, flexShrink: 0 }}>{timeAgo(c.last_message_at)}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 4 }}>
            <ChannelChip channel={c.primary_channel} size={11} />
            {c.is_starred && <Star size={11} fill={ig.warn} color={ig.warn} />}
            <span style={{ fontSize: 11.5, color: c.unread_count > 0 ? ig.ink : ig.muted,
              fontWeight: c.unread_count > 0 ? 700 : 500,
              flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {c.last_message}
            </span>
            {c.unread_count > 0 && (
              <span style={{
                background: ig.emerald, color: "#fff", borderRadius: 999, padding: "1px 7px",
                fontSize: 10, fontWeight: 800, flexShrink: 0,
              }}>{c.unread_count}</span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 7, flexWrap: "wrap" }}>
            <StatusBadge status={c.status} />
            {c.priority === "urgent" && <Badge tone="error">Urgent</Badge>}
            {c.priority === "important" && <Badge tone="warn">Important</Badge>}
            {c.ai_status === "draft_ready" && <Badge tone="emerald"><Sparkles size={9} /> AI ready</Badge>}
            {c.ai_status === "ai_suggested" && <Badge tone="emerald"><Bot size={9} /> AI</Badge>}
            {c.human_takeover_required && <Badge tone="error"><ShieldAlert size={9} /> Human</Badge>}
            {(c.related_ticket_ids || []).length > 0 && <Badge tone="info"><FileText size={9} /> Ticket</Badge>}
            {(contact.tags || []).slice(0, 1).map((t) => <Tag key={t}>{t}</Tag>)}
          </div>
        </div>
      </div>
    </button>
  );
}

/* ─────────────────────────── CENTER COLUMN ─────────────────────────── */

function CenterColumn({ convId, onReload }) {
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState(null);

  const loadAll = useCallback(() => {
    if (!convId) return;
    api.get(`/api/inbox/conversations/${convId}`).then(setConv).catch(() => setConv(null));
    api.get(`/api/inbox/conversations/${convId}/messages`).then((d) => setMessages(d.items)).catch(() => setMessages([]));
    api.post(`/api/inbox/conversations/${convId}/read`).catch(() => {});
  }, [convId]);

  useEffect(() => { setConv(null); setMessages(null); loadAll(); }, [convId, loadAll]);

  if (!convId) {
    return (
      <div style={{ display: "grid", placeItems: "center", background: ig.bg }}>
        <div style={{ textAlign: "center", color: ig.muted, padding: 24, maxWidth: 360 }}>
          <div style={{ width: 64, height: 64, borderRadius: 18, margin: "0 auto 14px",
            display: "grid", placeItems: "center",
            background: `linear-gradient(135deg, ${ig.mint}, #FFF)`,
            border: `1px solid ${ig.border}` }}>
            <MessageSquare size={26} color={ig.emerald} />
          </div>
          <div style={{ fontSize: 16, fontWeight: 800, color: ig.ink }}>Select a conversation</div>
          <div style={{ fontSize: 12.5, marginTop: 6, lineHeight: 1.6 }}>
            Pick a conversation on the left to view messages, contact details, tickets, and activity.
          </div>
        </div>
      </div>
    );
  }

  return (
    <section style={{ background: ig.bg, display: "flex", flexDirection: "column", minWidth: 0 }}>
      <ConversationHeader conv={conv} onReload={() => { loadAll(); onReload(); }} />
      <MessageTimeline messages={messages} />
      <MessageComposer convId={convId} conv={conv} onSent={() => { loadAll(); onReload(); }} />
    </section>
  );
}

function ConversationHeader({ conv, onReload }) {
  if (!conv) return (
    <div style={{ padding: "14px 18px", background: ig.card, borderBottom: `1px solid ${ig.border}` }}>
      <Skeleton height={22} width={240} />
    </div>
  );
  const c = conv.contact || {};

  const patch = async (body) => {
    try { await api.patch(`/api/inbox/conversations/${conv.id}`, body); onReload(); }
    catch (e) { alert(e.message); }
  };

  return (
    <div style={{ padding: "12px 18px", background: ig.card, borderBottom: `1px solid ${ig.border}`,
      display: "flex", alignItems: "center", gap: 12 }}>
      <Avatar name={c.full_name} size={38} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: ig.ink }}>{c.full_name || "—"}</span>
          <ChannelChip channel={conv.primary_channel} size={12} />
          <StatusBadge status={conv.status} />
          {conv.priority !== "normal" && <Badge tone={conv.priority === "urgent" ? "error" : "warn"}>{conv.priority}</Badge>}
        </div>
        <div style={{ fontSize: 11.5, color: ig.muted, marginTop: 3, display: "flex", gap: 12 }}>
          <span>{c.phone}</span><span>{c.email}</span><span>{c.source}</span>
        </div>
      </div>
      <IconBtn title={conv.is_starred ? "Unstar" : "Star"} active={conv.is_starred}
        data-testid="conv-star" onClick={() => patch({ is_starred: !conv.is_starred })}>
        <Star size={14} fill={conv.is_starred ? ig.warn : "none"} color={conv.is_starred ? ig.warn : ig.ink} />
      </IconBtn>
      <IconBtn title="Call" data-testid="conv-call"><Phone size={14} /></IconBtn>
      <IconBtn title="SMS"  data-testid="conv-sms"><MessageSquare size={14} /></IconBtn>
      <IconBtn title="Email" data-testid="conv-email"><Mail size={14} /></IconBtn>
      <select data-testid="conv-status" value={conv.status}
        onChange={(e) => patch({ status: e.target.value })}
        style={{ height: 32, padding: "0 8px", borderRadius: 9, border: `1px solid ${ig.border}`,
          background: ig.card, fontSize: 12, color: ig.ink, outline: "none" }}>
        {["new","open","needs_reply","waiting","replied","needs_follow_up","booked","in_review","closed","escalated"]
          .map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
      </select>
      <IconBtn title="More" data-testid="conv-more"><MoreHorizontal size={14} /></IconBtn>
    </div>
  );
}

function MessageTimeline({ messages }) {
  if (!messages) {
    return (
      <div className="ig-scroll" style={{ flex: 1, overflowY: "auto", padding: 24 }}>
        {[0,1,2].map((i) => (<div key={i} style={{ marginBottom: 14 }}><Skeleton height={48} radius={12} /></div>))}
      </div>
    );
  }
  if (messages.length === 0) {
    return (
      <div style={{ flex: 1, display: "grid", placeItems: "center", color: ig.muted, padding: 24 }}>
        <div style={{ textAlign: "center" }}>
          <MessageSquare size={28} color={ig.mutedSoft} />
          <div style={{ marginTop: 8, fontWeight: 700, color: ig.ink }}>No messages yet</div>
          <div style={{ fontSize: 12 }}>Start the conversation from the composer below.</div>
        </div>
      </div>
    );
  }

  // Group with date dividers
  const out = [];
  let lastDay = null;
  for (const m of messages) {
    const day = dayLabel(m.created_at);
    if (day !== lastDay) {
      out.push(<DateDivider key={`d-${m.id}`} label={day} />);
      lastDay = day;
    }
    out.push(<MessageBubble key={m.id} m={m} />);
  }

  return (
    <div className="ig-scroll" style={{ flex: 1, overflowY: "auto", padding: "18px 24px" }}>
      {out}
    </div>
  );
}

function DateDivider({ label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", margin: "16px 0", gap: 10 }}>
      <div style={{ flex: 1, height: 1, background: ig.borderSoft }} />
      <span style={{ fontSize: 11, color: ig.muted, fontWeight: 700, letterSpacing: 0.2 }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: ig.borderSoft }} />
    </div>
  );
}

function MessageBubble({ m }) {
  if (m.is_internal_note || m.channel === "system" || m.channel === "internal") {
    return (
      <div className="ig-fade-in" style={{ margin: "10px 0", display: "flex", justifyContent: "center" }}>
        <div style={{
          maxWidth: 580, padding: "9px 14px", borderRadius: 10,
          background: "#FFF8E1", border: `1px dashed #F0C674`, fontSize: 12, color: "#7C5610",
          display: "flex", gap: 8, alignItems: "flex-start",
        }}>
          <CircleHelp size={13} style={{ marginTop: 2, flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", marginBottom: 2 }}>
              {m.channel === "system" ? "System event" : "Internal note"}
            </div>
            {m.body}
            <div style={{ fontSize: 10, color: "#A77A2B", marginTop: 4 }}>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
          </div>
        </div>
      </div>
    );
  }

  if (m.channel === "voicemail") {
    return <VoicemailCard m={m} />;
  }
  if (m.channel === "phone") {
    return <CallCard m={m} />;
  }

  const outbound = m.direction === "outbound";
  return (
    <div className="ig-fade-in" style={{ margin: "10px 0", display: "flex", justifyContent: outbound ? "flex-end" : "flex-start" }}>
      <div style={{ maxWidth: 620 }}>
        {m.subject && (
          <div style={{ fontSize: 11, color: ig.muted, marginBottom: 4, fontWeight: 600 }}>
            <Mail size={10} style={{ marginRight: 4 }} /> {m.subject}
          </div>
        )}
        <div style={{
          padding: "10px 14px", borderRadius: 12,
          background: outbound ? `linear-gradient(135deg, ${ig.emerald}, ${ig.green})` : ig.card,
          color: outbound ? "#fff" : ig.ink,
          border: outbound ? "0" : `1px solid ${ig.border}`,
          boxShadow: outbound ? "0 4px 14px rgba(24,184,107,0.22)" : ig.shadow,
          fontSize: 13.5, lineHeight: 1.55, whiteSpace: "pre-wrap",
        }}>
          {m.body}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4,
          justifyContent: outbound ? "flex-end" : "flex-start" }}>
          <ChannelChip channel={m.channel} size={10} />
          <span style={{ fontSize: 10.5, color: ig.muted }}>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
          {m.is_ai_generated && <Badge tone="emerald"><Sparkles size={8} /> AI</Badge>}
          {m.delivery_status === "failed" && <Badge tone="error">Failed</Badge>}
        </div>
      </div>
    </div>
  );
}

function VoicemailCard({ m }) {
  const meta = m.metadata || {};
  return (
    <div className="ig-fade-in" style={{ margin: "12px 0" }}>
      <div style={{ maxWidth: 560, padding: 14, borderRadius: 14, background: ig.card,
        border: `1px solid ${ig.border}`, boxShadow: ig.shadow }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <span style={{ width: 36, height: 36, borderRadius: 10, background: "#FEF3C7",
            display: "grid", placeItems: "center", color: "#92400E" }}><Voicemail size={16} /></span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 800 }}>Voicemail · {meta.duration_seconds || 0}s</div>
            <div style={{ fontSize: 11, color: ig.muted }}>{new Date(m.created_at).toLocaleString()}</div>
          </div>
          <GhostBtn data-testid="vm-callback"><Phone size={12} /> Call back</GhostBtn>
        </div>
        <div style={{ padding: 10, borderRadius: 9, background: ig.bg, border: `1px solid ${ig.borderSoft}`, fontSize: 12.5, lineHeight: 1.55 }}>
          “{meta.transcription || m.body}”
        </div>
        {meta.ai_summary && (
          <div style={{ marginTop: 8, padding: 10, borderRadius: 9, background: "#ECFCF3",
            border: `1px solid #BFEED5`, fontSize: 11.5, color: ig.forest, display: "flex", gap: 8 }}>
            <Sparkles size={12} color={ig.emerald} style={{ marginTop: 2, flexShrink: 0 }} />
            <div><strong>AI summary:</strong> {meta.ai_summary}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function CallCard({ m }) {
  return (
    <div className="ig-fade-in" style={{ margin: "12px 0", display: "flex", justifyContent: "center" }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "8px 12px",
        borderRadius: 999, background: ig.card, border: `1px solid ${ig.border}`, fontSize: 12 }}>
        <PhoneMissed size={13} color={ig.warn} />
        <span style={{ fontWeight: 700 }}>{m.body}</span>
        <span style={{ color: ig.muted }}>· {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
      </div>
    </div>
  );
}

/* ─────────────────────────── COMPOSER ─────────────────────────── */

const MODES = [
  { key: "sms",       label: "SMS",       hint: "160-char SMS",          icon: MessageSquare },
  { key: "email",     label: "Email",     hint: "Subject + body",        icon: Mail },
  { key: "whatsapp",  label: "WhatsApp",  hint: "WhatsApp message",      icon: MessageSquare },
  { key: "internal",  label: "Note",      hint: "Private internal note", icon: CircleHelp },
];

function MessageComposer({ convId, conv, onSent }) {
  const [mode, setMode] = useState("sms");
  const [body, setBody] = useState("");
  const [subject, setSubject] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [snipOpen, setSnipOpen] = useState(false);
  const [snips, setSnips] = useState([]);
  const [tone, setTone] = useState("friendly");
  const [aiDraft, setAiDraft] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiMeta, setAiMeta] = useState(null);

  // Pre-select mode based on conversation channel
  useEffect(() => {
    if (conv?.primary_channel === "email") setMode("email");
    else if (conv?.primary_channel === "whatsapp") setMode("whatsapp");
    else if (conv?.primary_channel === "phone") setMode("sms");
    else if (conv?.primary_channel === "instagram") setMode("whatsapp"); // closest available
  }, [conv?.primary_channel]);

  useEffect(() => {
    api.get("/api/inbox/snippets").then((d) => setSnips(d.items || []));
  }, []);

  if (!convId) return null;
  const contact = conv?.contact || {};
  const dnd = contact.dnd_status;
  const isInternal = mode === "internal";
  const isEmail = mode === "email";

  const send = async (asAI = false) => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post(`/api/inbox/conversations/${convId}/messages`, {
        channel: isInternal ? "internal" : mode,
        body,
        subject: isEmail ? subject : null,
        is_internal_note: isInternal,
        is_ai_generated: asAI,
      });
      setBody(""); setSubject(""); setAiDraft(null); setAiMeta(null);
      onSent();
    } catch (e) {
      alert(e.message);
    } finally { setBusy(false); }
  };

  const generateAI = async () => {
    setAiBusy(true);
    try {
      const res = await api.post("/api/inbox/ai/reply", { conversation_id: convId, tone });
      setAiDraft(res.reply);
      setAiMeta(res);
    } catch (e) { alert(e.message); }
    finally { setAiBusy(false); }
  };

  const insertSnippet = (s) => {
    const merged = s.body
      .replace(/\{\{contact\.first_name\}\}/g, contact.first_name || "there")
      .replace(/\{\{contact\.full_name\}\}/g, contact.full_name || "")
      .replace(/\{\{contact\.last_name\}\}/g, contact.last_name || "")
      .replace(/\{\{contact\.email\}\}/g, contact.email || "")
      .replace(/\{\{contact\.phone\}\}/g, contact.phone || "");
    setBody((b) => (b ? b + "\n\n" + merged : merged));
    setSnipOpen(false);
  };

  return (
    <div style={{
      borderTop: `1px solid ${ig.border}`, background: ig.card, padding: "10px 18px 14px",
      position: "sticky", bottom: 0,
    }}>
      {/* Top row — mode selector + actions */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        {MODES.map((m) => {
          const active = mode === m.key;
          const M = m.icon;
          return (
            <button key={m.key} data-testid={`composer-mode-${m.key}`} onClick={() => setMode(m.key)} style={{
              padding: "5px 10px", borderRadius: 8, border: `1px solid ${active ? ig.emerald : ig.border}`,
              background: active ? ig.mint : ig.card, color: active ? ig.forest : ig.ink,
              fontSize: 11.5, fontWeight: 700, cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 5,
            }}>
              <M size={11} /> {m.label}
            </button>
          );
        })}
        <div style={{ flex: 1 }} />
        <SnippetPicker open={snipOpen} setOpen={setSnipOpen} snips={snips} onPick={insertSnippet} />
        <GhostBtn data-testid="composer-ai" onClick={() => setAiOpen((v) => !v)}>
          <Sparkles size={12} color={ig.emerald} /> AI Reply
        </GhostBtn>
        <IconBtn title="Attach" data-testid="composer-attach"><Paperclip size={13} /></IconBtn>
        <IconBtn title="Schedule" data-testid="composer-schedule"><Clock size={13} /></IconBtn>
      </div>

      {/* DND warning */}
      {dnd && !isInternal && (mode === "sms" || mode === "whatsapp") && (
        <div style={{ padding: "8px 12px", marginBottom: 8, borderRadius: 9,
          background: "#FEE2E2", border: "1px solid #FECACA", color: "#991B1B", fontSize: 11.5,
          display: "flex", gap: 8, alignItems: "center" }}>
          <AlertOctagon size={13} />
          This contact has opted out or is marked DND for this channel. You cannot send this message unless consent is updated.
        </div>
      )}

      {/* AI draft preview */}
      {aiOpen && (
        <div style={{ marginBottom: 10, padding: 12, borderRadius: 12, background: "#F5FCF8",
          border: `1px solid ${ig.emerald}`, position: "relative" }} className="ig-fade-in">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <span style={{ width: 24, height: 24, borderRadius: 7, background: ig.emerald, color: "#fff",
              display: "grid", placeItems: "center" }}><Sparkles size={12} /></span>
            <div style={{ fontWeight: 800, fontSize: 12, color: ig.forest }}>AI Reply assistant</div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              {["friendly","professional","short","warm","booking","objection","follow_up"].map((t) => (
                <button key={t} data-testid={`ai-tone-${t}`} onClick={() => setTone(t)} style={{
                  padding: "3px 8px", borderRadius: 999, border: `1px solid ${tone === t ? ig.emerald : ig.border}`,
                  background: tone === t ? ig.mint : ig.card, color: tone === t ? ig.forest : ig.muted,
                  fontSize: 10.5, fontWeight: 700, cursor: "pointer",
                }}>{t}</button>
              ))}
            </div>
            <IconBtn title="Close" onClick={() => setAiOpen(false)}><X size={12} /></IconBtn>
          </div>
          {aiDraft ? (
            <div>
              <div style={{ padding: 10, borderRadius: 9, background: ig.card, border: `1px solid ${ig.border}`,
                fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.55 }}>{aiDraft}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                {aiMeta?.detected_language && <Badge tone="emerald"><Languages size={9} /> {aiMeta.detected_language.toUpperCase()}</Badge>}
                {aiMeta?.detected_intent && <Badge tone="info">{aiMeta.detected_intent}</Badge>}
                {aiMeta?.human_takeover_recommended && <Badge tone="error"><ShieldAlert size={9} /> Human takeover suggested</Badge>}
                <span style={{ fontSize: 10.5, color: ig.muted, marginLeft: "auto" }}>Confidence {Math.round((aiMeta?.confidence || 0) * 100)}%</span>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <PrimaryBtn data-testid="ai-use" onClick={() => { setBody(aiDraft); setAiOpen(false); }}>Use draft</PrimaryBtn>
                <GhostBtn data-testid="ai-regenerate" onClick={generateAI} disabled={aiBusy}>{aiBusy ? "Generating…" : "Regenerate"}</GhostBtn>
                <GhostBtn data-testid="ai-send-direct" onClick={() => { setBody(aiDraft); setTimeout(() => send(true), 100); }}>Use &amp; send</GhostBtn>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 12, color: ig.muted, marginBottom: 8 }}>
                Generate a reply tailored to <strong style={{ color: ig.ink }}>{contact.full_name || "this contact"}</strong> · channel <strong>{conv?.primary_channel}</strong>. AI drafts only — nothing sends until you approve.
              </div>
              <PrimaryBtn data-testid="ai-generate" onClick={generateAI} disabled={aiBusy}>
                <Sparkles size={13} /> {aiBusy ? "Generating draft…" : "Generate reply"}
              </PrimaryBtn>
            </div>
          )}
        </div>
      )}

      {/* Subject (email only) */}
      {isEmail && (
        <input data-testid="composer-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject" style={{ width: "100%", height: 34, padding: "0 12px", borderRadius: 9,
            border: `1px solid ${ig.border}`, background: ig.bg, fontSize: 13, marginBottom: 8, outline: "none", color: ig.ink }} />
      )}

      {/* Composer textarea */}
      <div style={{
        position: "relative", borderRadius: 12,
        background: isInternal ? "#FFF8E1" : ig.bg,
        border: `1px solid ${isInternal ? "#F0C674" : ig.border}`,
      }}>
        <textarea data-testid="composer-body" value={body} onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(false); } }}
          placeholder={isInternal ? "Add a private note (not sent to client)…" :
            mode === "sms" ? "Type SMS…" :
            mode === "email" ? "Write your email…" :
            mode === "whatsapp" ? "Type WhatsApp message…" : "Type a message…"}
          rows={isEmail ? 5 : 3}
          style={{ width: "100%", padding: "11px 14px 36px", border: 0, borderRadius: 12,
            background: "transparent", resize: "vertical", outline: "none",
            fontSize: 13.5, lineHeight: 1.55, fontFamily: "inherit", color: ig.ink, minHeight: 78 }} />
        <div style={{ position: "absolute", left: 12, bottom: 9, fontSize: 10.5, color: ig.muted, display: "flex", gap: 12 }}>
          {mode === "sms" && <span data-testid="sms-counter">{body.length} chars · {Math.max(1, Math.ceil(body.length / 160))} segment{Math.ceil(body.length / 160) > 1 ? "s" : ""}</span>}
          {isInternal && <span><CircleHelp size={9} style={{ marginRight: 4 }} /> Private — not sent to client</span>}
        </div>
        <div style={{ position: "absolute", right: 8, bottom: 8, display: "flex", gap: 6 }}>
          <PrimaryBtn data-testid="composer-send" onClick={() => send(false)} disabled={busy || !body.trim() || (dnd && (mode === "sms" || mode === "whatsapp") && !isInternal)}>
            <Send size={12} /> {busy ? "Sending…" : isInternal ? "Save note" : "Send"}
          </PrimaryBtn>
        </div>
      </div>
    </div>
  );
}

function SnippetPicker({ open, setOpen, snips, onPick }) {
  return (
    <div style={{ position: "relative" }}>
      <GhostBtn data-testid="composer-snippets" onClick={() => setOpen((v) => !v)}>
        <TagIcon size={12} /> Snippets
      </GhostBtn>
      {open && (
        <div style={{
          position: "absolute", right: 0, bottom: "calc(100% + 6px)",
          width: 320, maxHeight: 360, overflowY: "auto",
          background: ig.card, border: `1px solid ${ig.border}`, borderRadius: 12,
          boxShadow: ig.shadowLg, zIndex: 10, padding: 8,
        }} className="ig-scroll ig-fade-in">
          {snips.length === 0 ? <div style={{ padding: 12, color: ig.muted, fontSize: 12 }}>No snippets.</div> :
            snips.map((s) => (
              <button key={s.id} data-testid={`snippet-${s.id}`} onClick={() => onPick(s)} style={{
                display: "block", width: "100%", textAlign: "left", padding: "10px 12px",
                borderRadius: 8, border: 0, background: "transparent", cursor: "pointer",
              }}
                onMouseEnter={(e) => { e.currentTarget.style.background = ig.mint; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: ig.ink }}>{s.name}</span>
                  <Badge tone="emerald">{s.language || "en"}</Badge>
                </div>
                <div style={{ fontSize: 11.5, color: ig.muted, lineHeight: 1.5,
                  display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                  overflow: "hidden" }}>{s.body}</div>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

const rightEmptyStyle = {
  borderLeft: `1px solid ${ig.border}`, background: ig.card,
  display: "grid", placeItems: "center", padding: 20,
};
